import { useJssStore } from '../stores/jssStore';

export const VAR_DARK = 'var-dark' as const;
export const VAR_CREME = 'var-creme' as const;

export const themes = {
  THEME_BRAND: 'theme-brand',
  THEME_LIGHT: 'theme-light',
  THEME_DARK: 'theme-dark',
  THEME_ALTERNATIVE: 'theme-alternative',
  THEME_CORPORATE: 'theme-corporate',
  THEME_VENUE: 'theme-corporate-venue',
} as const;

export type ThemeKey = (typeof themes)[keyof typeof themes];

export const variantedComponents = {
  CONTACT_US: 'CONTACT_US',
  MAIN_HEADER: 'MAIN_HEADER',
  MOBILE_MENU: 'MOBILE_MENU',
  MOBILE_MENU_FOOTER: 'MOBILE_MENU_FOOTER',
  INTRODUCTION_IMAGE: 'INTRODUCTION_IMAGE',
  INTRODUCTION_ITEM: 'INTRODUCTION_ITEM',
  PUB_INFO_BAR: 'PUB_INFO_BAR',
  PROMO_CAROUSEL_ITEM: 'PROMO_CAROUSEL_ITEM',
  SIGNUP_BANNER: 'SIGNUP_BANNER',
  NEWSLETTER_BLOCK: 'NEWSLETTER_BLOCK',
  OPENING_HOURS: 'OPENING_HOURS',
  SIGNUP_BANNER_MODAL: 'SIGNUP_BANNER_MODAL',
  NEWSLETTER_BLOCK_MODAL: 'NEWSLETTER_BLOCK_MODAL',
  DOWNLOAD_LINKS_MODAL: 'DOWNLOAD_LINKS_MODAL',
  FACILITIES_BLOCK: 'FACILITIES_BLOCK',
  MENUS_DOWNLOAD: 'MENUS_DOWNLOAD',
  FIXED_PROMO_MESSAGE: 'FIXED_PROMO_MESSAGE',
  MAIN_FOOTER: 'MAIN_FOOTER',
  PROMO_LIST: 'PROMO_LIST',
  HERO_CAROUSEL: 'HERO_CAROUSEL',
  SPORT_EVENTS_NAVIGATION: 'SPORT_EVENTS_NAVIGATION',
  IORDER_MENU_LEGACY_NAVIGATION: 'IORDER_MENU_LEGACY_NAVIGATION',
  SUBNAVIGATION: 'SUBNAVIGATION',
} as const;

export type VariantedComponent = (typeof variantedComponents)[keyof typeof variantedComponents];

const brands = {
  CRAFTED: 'Crafted',
  METRO: 'Metro',
  GREENE_KING: 'GreeneKing',
  HUNGRY_HORSE: 'HungryHorse',
  FARMHOUSE_INNS: 'FarmhouseInns',
  CHEF_BREWER: 'ChefBrewer',
  WACKY_WAREHOUSE: 'WackyWarehouse',
  GREENE_KING_INNS: 'GreeneKingInns',
  BELHAVEN: 'Belhaven',
  EVERLY: 'Everly',
  FARMHOUSE_KITCHEN: 'FarmhouseKitchen',
  PUB_PARTNERS: 'PubPartners',
  FLAMING_GRILL: 'FlamingGrill',
  CORPORATE: 'Corporate',
  CORNERSTONE: 'Cornerstone',
} as const;

type VariantResult = Record<string, boolean | undefined>;

export const componentDarkVariantMap = {
  [variantedComponents.INTRODUCTION_IMAGE]: [brands.BELHAVEN],
  [variantedComponents.INTRODUCTION_ITEM]: [brands.BELHAVEN],
  [variantedComponents.PUB_INFO_BAR]: [
    brands.CRAFTED,
    brands.FARMHOUSE_INNS,
    brands.GREENE_KING_INNS,
    brands.WACKY_WAREHOUSE,
    brands.GREENE_KING,
    brands.FLAMING_GRILL,
  ],
  [variantedComponents.PROMO_CAROUSEL_ITEM]: [brands.CRAFTED, brands.HUNGRY_HORSE],
  [variantedComponents.SIGNUP_BANNER]: [
    brands.FARMHOUSE_INNS,
    brands.WACKY_WAREHOUSE,
    brands.BELHAVEN,
    brands.PUB_PARTNERS,
  ],
  [variantedComponents.SIGNUP_BANNER_MODAL]: [
    brands.CRAFTED,
    brands.FARMHOUSE_INNS,
    brands.CHEF_BREWER,
    brands.GREENE_KING_INNS,
  ],
  [variantedComponents.NEWSLETTER_BLOCK_MODAL]: [
    brands.CRAFTED,
    brands.FARMHOUSE_INNS,
    brands.CHEF_BREWER,
    brands.GREENE_KING_INNS,
  ],
  [variantedComponents.DOWNLOAD_LINKS_MODAL]: [brands.CRAFTED],
  [variantedComponents.OPENING_HOURS]: [
    brands.CRAFTED,
    brands.WACKY_WAREHOUSE,
    brands.METRO,
    brands.FARMHOUSE_INNS,
    brands.CHEF_BREWER,
    brands.GREENE_KING_INNS,
    brands.BELHAVEN,
    brands.EVERLY,
    brands.FARMHOUSE_KITCHEN,
    brands.PUB_PARTNERS,
    brands.HUNGRY_HORSE,
    brands.GREENE_KING,
    brands.CORPORATE,
    brands.CORNERSTONE,
  ],
  [variantedComponents.FACILITIES_BLOCK]: [brands.BELHAVEN],
  [variantedComponents.MENUS_DOWNLOAD]: [brands.BELHAVEN],
  [variantedComponents.FIXED_PROMO_MESSAGE]: [brands.CRAFTED, brands.GREENE_KING, brands.CORPORATE],
  [variantedComponents.MAIN_FOOTER]: [
    brands.CRAFTED,
    brands.METRO,
    brands.HUNGRY_HORSE,
    brands.FARMHOUSE_INNS,
    brands.CHEF_BREWER,
    brands.WACKY_WAREHOUSE,
    brands.BELHAVEN,
    brands.GREENE_KING_INNS,
    brands.PUB_PARTNERS,
    brands.FLAMING_GRILL,
    brands.GREENE_KING,
    brands.CORPORATE,
    brands.CORNERSTONE,
  ],
  [variantedComponents.HERO_CAROUSEL]: [
    brands.CRAFTED,
    brands.METRO,
    brands.HUNGRY_HORSE,
    brands.FARMHOUSE_INNS,
    brands.CHEF_BREWER,
    brands.WACKY_WAREHOUSE,
    brands.BELHAVEN,
    brands.GREENE_KING_INNS,
    brands.EVERLY,
    brands.FARMHOUSE_KITCHEN,
    brands.PUB_PARTNERS,
    brands.FLAMING_GRILL,
    brands.GREENE_KING,
    brands.CORPORATE,
    brands.CORNERSTONE,
  ],
  [variantedComponents.MAIN_HEADER]: [brands.PUB_PARTNERS, brands.CORPORATE],
  [variantedComponents.MOBILE_MENU]: [brands.PUB_PARTNERS, brands.CORPORATE],
  [variantedComponents.MOBILE_MENU_FOOTER]: [brands.CORPORATE],
  [variantedComponents.IORDER_MENU_LEGACY_NAVIGATION]: [brands.FLAMING_GRILL, brands.CORNERSTONE],
  [variantedComponents.SUBNAVIGATION]: [brands.FLAMING_GRILL, brands.CORNERSTONE],
  [variantedComponents.SPORT_EVENTS_NAVIGATION]: [brands.GREENE_KING, brands.CORNERSTONE],
};

export const componentCremeVariantMap = {
  [variantedComponents.CONTACT_US]: [brands.CHEF_BREWER, brands.WACKY_WAREHOUSE],
  [variantedComponents.SPORT_EVENTS_NAVIGATION]: [brands.CRAFTED, brands.GREENE_KING_INNS],
};

export const useDetectVariantByBrand = (): ((component: string) => VariantResult) => {
  const jssStore = useJssStore();

  return (component: string): VariantResult => ({
    [VAR_DARK]: componentDarkVariantMap[component]?.includes(jssStore.brandName),
    [VAR_CREME]: componentCremeVariantMap[component]?.includes(jssStore.brandName),
  });
};

export const componentVariantByThemeMap = {
  [variantedComponents.MAIN_HEADER]: {
    [themes.THEME_BRAND]: [brands.METRO],
    [themes.THEME_DARK]: [brands.METRO],
    [themes.THEME_ALTERNATIVE]: [brands.METRO],
    [themes.THEME_CORPORATE]: [
      brands.HUNGRY_HORSE,
      brands.FARMHOUSE_INNS,
      brands.CHEF_BREWER,
      brands.WACKY_WAREHOUSE,
      brands.GREENE_KING_INNS,
      brands.BELHAVEN,
      brands.FLAMING_GRILL,
      brands.GREENE_KING,
      brands.CORNERSTONE,
    ],
  },
  [variantedComponents.MOBILE_MENU]: {
    [themes.THEME_BRAND]: [brands.METRO],
    [themes.THEME_DARK]: [brands.METRO],
    [themes.THEME_ALTERNATIVE]: [brands.METRO],
    [themes.THEME_CORPORATE]: [
      brands.FARMHOUSE_INNS,
      brands.WACKY_WAREHOUSE,
      brands.GREENE_KING_INNS,
      brands.BELHAVEN,
      brands.FLAMING_GRILL,
      brands.GREENE_KING,
      brands.CORNERSTONE,
    ],
  },
  [variantedComponents.MOBILE_MENU_FOOTER]: {
    [themes.THEME_CORPORATE]: [brands.FLAMING_GRILL, brands.CORNERSTONE],
  },
  [variantedComponents.PROMO_CAROUSEL_ITEM]: {
    [themes.THEME_CORPORATE]: [brands.BELHAVEN],
  },
  [variantedComponents.FIXED_PROMO_MESSAGE]: {
    [themes.THEME_BRAND]: [brands.METRO, brands.GREENE_KING_INNS],
    [themes.THEME_DARK]: [brands.METRO],
    [themes.THEME_ALTERNATIVE]: [brands.METRO],
    [themes.THEME_VENUE]: [
      brands.CHEF_BREWER,
      brands.WACKY_WAREHOUSE,
      brands.GREENE_KING_INNS,
      brands.BELHAVEN,
      brands.HUNGRY_HORSE,
    ],
  },
  [variantedComponents.PROMO_LIST]: {
    [themes.THEME_VENUE]: [brands.FARMHOUSE_INNS],
  },
  [variantedComponents.PUB_INFO_BAR]: {
    [themes.THEME_BRAND]: [brands.METRO],
    [themes.THEME_DARK]: [brands.METRO],
    [themes.THEME_ALTERNATIVE]: [brands.METRO],
  },
};

export const useDetectVariantByTheme = (): ((component: string) => VariantResult) => {
  const jssStore = useJssStore();

  return (component: string): VariantResult => ({
    [VAR_DARK]: componentVariantByThemeMap[component]?.[jssStore.theme]?.includes(
      jssStore.brandName
    ),
  });
};

export const useDetectVariantByBrandAndTheme = (): ((component: string) => VariantResult) => {
  const jssStore = useJssStore();

  return (component: string): VariantResult => ({
    [VAR_DARK]:
      componentDarkVariantMap[component]?.includes(jssStore.brandName) ||
      componentVariantByThemeMap[component]?.[jssStore.theme]?.includes(jssStore.brandName),
    [VAR_CREME]: componentCremeVariantMap[component]?.includes(jssStore.brandName),
  });
};
